import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Youtube, ArrowUpRight, Radio, Volume2, VolumeX } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHANNEL_ID = "UCxvmL3w8S2e6tTPN18sE5Fg";
const CHANNEL_URL = `https://www.youtube.com/channel/${CHANNEL_ID}`;
const LIVE_URL = `${CHANNEL_URL}/live`;

const FALLBACK_VIDEO_ID = "in20_cl-j3c";

function buildEmbedSrc(videoId) {
  // enablejsapi=1 so we can toggle mute via postMessage
  // mute=1 so the iframe can autoplay in browsers
  return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&enablejsapi=1&mute=1&autoplay=1&playsinline=1`;
}

export default function YouTubeLive() {
  const [videoId, setVideoId] = useState(FALLBACK_VIDEO_ID);
  const [isMuted, setIsMuted] = useState(true);
  const iframeRef = useRef(null);

  // Auto-fetch the most recent long-form upload on mount.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/latest-video?kind=long`)
      .then((r) => {
        const id = r?.data?.video_id;
        if (!cancelled && id && /^[A-Za-z0-9_-]{11}$/.test(id)) setVideoId(id);
      })
      .catch(() => {
        /* silently fall back to FALLBACK_VIDEO_ID */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleMute = () => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentWindow) return;
    const cmd = isMuted ? "unMute" : "mute";
    iframe.contentWindow.postMessage(
      JSON.stringify({ event: "command", func: cmd, args: [] }),
      "*",
    );
    setIsMuted((m) => !m);
  };

  const embed = buildEmbedSrc(videoId);

  return (
    <section
      id="live"
      data-testid="youtube-live-section"
      className="py-20 md:py-28 px-6 md:px-12 bg-[#070707]"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-12 reveal">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-gold" />
            <span className="text-xs uppercase tracking-overline text-zinc-500">
              01 — Latest Expeditions
            </span>
          </div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.02] tracking-tight max-w-3xl">
            The newest episode,{" "}
            <em className="italic text-gold">fresh from the road</em>.
          </h2>
        </div>

        {/* Player (full width) */}
        <div className="relative bg-black border border-white/10 aspect-video overflow-hidden reveal max-w-5xl mx-auto">
          <iframe
            ref={iframeRef}
            key={videoId}
            src={embed}
            title="Featured video"
            data-testid="yt-iframe"
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />

          {/* Floating mute/unmute toggle — bottom-right corner */}
          <button
            type="button"
            onClick={toggleMute}
            aria-label={isMuted ? "Unmute video" : "Mute video"}
            aria-pressed={!isMuted}
            data-testid="yt-mute-toggle"
            className="absolute bottom-4 right-4 z-10 inline-flex items-center gap-2 px-4 py-2.5 bg-black/70 backdrop-blur-md border border-white/15 hover:border-gold hover:text-gold text-zinc-100 text-[11px] uppercase tracking-overline transition-colors duration-300"
          >
            {isMuted ? (
              <>
                <VolumeX className="w-4 h-4" strokeWidth={1.75} />
                Unmute
              </>
            ) : (
              <>
                <Volume2 className="w-4 h-4 text-gold" strokeWidth={1.75} />
                Mute
              </>
            )}
          </button>
        </div>

        {/* CTAs below player */}
        <div className="mt-10 max-w-5xl mx-auto flex flex-col sm:flex-row gap-4 reveal">
          <a
            href={CHANNEL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="yt-subscribe-btn"
            className="group inline-flex flex-1 items-center justify-between gap-3 px-6 py-5 bg-gold text-black text-xs uppercase tracking-overline font-medium hover:bg-white transition-colors duration-300"
          >
            <span className="inline-flex items-center gap-3">
              <Youtube className="w-5 h-5" strokeWidth={1.5} />
              Subscribe to stay updated
            </span>
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href={LIVE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="yt-live-btn"
            className="group inline-flex flex-1 items-center justify-between gap-3 px-6 py-5 border border-white/20 text-zinc-200 text-xs uppercase tracking-overline hover:border-gold hover:text-gold transition-colors"
          >
            <span className="inline-flex items-center gap-3">
              <span className="relative flex w-2.5 h-2.5">
                <span className="absolute inset-0 bg-red-500/60 rounded-full animate-ping" />
                <span className="relative w-2.5 h-2.5 bg-red-500 rounded-full" />
              </span>
              <Radio className="w-4 h-4" strokeWidth={1.5} />
              Watch live stream
            </span>
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
      </div>
    </section>
  );
}
